<template>
  <Directory
    class="jobs"
    v-bind="{
      activeFilters,
      inputText: filterValues.input_text,
      isLoading,
      results,
      resultCount,
      unitLabels
    }"
    @text-input="filterValues.input_text = $event"
    @clear-all="clearAllFilters"
    @clear-filter="clearFilter"
    @more-click="fetchNextPage"
  >
    <div class="jobs__filters-list" slot="filters">
      <CmSelect
        v-for="filter in filters"
        class="jobs__filter jobs__filter--select"
        :key="filter.id"
        :name="filter.id"
        :options="filter.options"
        :null-label="filter.nullLabel"
        :label="filter.label"
        :multiple="filter.multiple"
        :include-search="filter.options.length > 12"
        :value="filterValues[filter.id]"
        @input="filterValues[filter.id] = $event"
        compact
      />
      <LocationFilter
        class="jobs__filter jobs__filter--select"
        v-model="filterValues.location"
        :location="filterValues.location"
        :radius="filterValues.radius"
        @location="filterValues.location = $event"
        @coords="filterValues.coords = $event"
        @radius="filterValues.radius = $event"
      />
      <Checkbox
        class="jobs__filter jobs__filter--checkbox"
        v-model="filterValues.remote_friendly"
        label="Remote friendly"
      />
    </div>

    <div class="jobs__list" :class="{
        'jobs__list--is-loading': isLoading,
      }">
      <Job
        class="jobs__job"
        v-for="result in results"
        :key="result.id"
        :job="result"
        :show="{date: true, motto: true}"
        :company="result.company"
        context="search"
      />
    </div>
  </Directory>
</template>

<script>
// If there's a variable in the template or in the JS that you can't find,
// CHECK THE MIXIN

import Job from "../company/job";
import DirectoryMixin from "../mixins/directory";

import { preprocessQueryString } from "../lib/elastic-search";
import { getURLParams } from "../lib/url";

export default {
  mixins: [DirectoryMixin],

  components: {
    Job
  },

  data() {
    let filters = this.generateFilters();

    return {
      filterArray: filters,
      defaultValues: this.generateDefaultFilterValues(filters),
      filterValues: this.generateFilterValues(filters),
      unitLabels: {
        singular: "Job",
        plural: "Jobs"
      },
      aggregatedHits: [],

      index: "jobs"
    };
  },

  computed: {
    filterKeys() {
      return [
        "remote_friendly",
        "skill_tag_id",
        "commitment",
        "location",
        "coords",
        "company_id"
      ];
    },

    activeFilters() {
      const values = this.filterValues;
      const defaults = this.defaultValues;

      return [
        {
          label: "Remote Friendly",
          keys: ["remote_friendly"],
          value: values.remote_friendly
        },
        {
          keys: ["company_id"],
          value: values.company_id,
          label:
            "from " +
            ((
              (
                this.results.find(
                  result => result.company.id === parseInt(values.company_id)
                ) || {}
              ).company || {}
            ).name || "...")
        },
        ...values.skill_tag_id.map(filterValue => ({
          keys: ["skill_tag_id"],
          match: filterValue,
          value: filterValue,
          label: (
            this.filters
              .find(propsFilter => propsFilter.id === "skill_tag_id")
              .options.find(option => option.value === filterValue) || {}
          ).label
        })),
        ...values.commitment.map(filterValue => ({
          keys: ["commitment"],
          match: filterValue,
          value: filterValue,
          label: (
            this.filters
              .find(propsFilter => propsFilter.id === "commitment")
              .options.find(option => option.value === filterValue) || {}
          ).label
        })),
        values.coords.lat &&
          values.coords.lng && {
            keys: ["location", "coords"],
            label: `Within ${values.radius} of ${values.location}`
          }
      ]
        .filter(Boolean)
        .filter(filter => filter.value !== defaults[filter.keys[0]]);
    },

    anyfiltersActive() {
      const values = this.filterValues;
      const defaults = this.defaultValues;

      return (
        [
          ["remote_friendly", values.remote_friendly],
          ["company_id", values.company_id],
          values.skill_tag_id.length > 0 && [
            "skill_tag_id",
            values.skill_tag_id
          ],
          values.commitment.length > 0 && ["commitment", values.commitment],
          values.coords.lat &&
            values.coords.lng && [
              "location",
              [values.coords.lat, values.coords.lng]
            ]
        ]
          .filter(Boolean)
          .filter(filter => filter[1] !== defaults[filter[0]]).length > 0
      );
    },

    noFiltersApplied() {
      return this.filterValues.input_text.length == 0 && !this.anyfiltersActive;
    },

    results() {
      if (this.response) {
        const mapSource = el => el._source;

        const hits = this.response.hits.hits.map(mapSource)

        if (
          this.noFiltersApplied &&
          this.response.aggregations &&
          this.response.aggregations.top_per_company
        ) {
          if (this.aggregatedHits.length == 0) {
            this.aggregatedHits = this.response.aggregations.top_per_company.buckets
              .map(x => x.tops.hits.hits)
              .reduce((acc, val) => acc.concat(val), [])
              .map(mapSource)
              .sort(() => Math.random() - 0.5);
          }

          const dedupedJobs = hits
            .filter(hit => {
              return !this.aggregatedHits
                .map(aggJob => aggJob.id)
                .includes(hit.id);
            });

          const aggedResults = [...this.aggregatedHits, ...dedupedJobs];

          return aggedResults.slice(0, (this.page + 1) * 15);
        } else {
          return hits;
        }
      } else return [];
    },

    query() {
      const filters = this.filterValues;
      const sortValues = () => {
        return [
          filters.sort_key === "recently_updated" && {
            updated_at: "desc"
          },
          filters.sort_key === "newest" && { created_at: "desc" },
          "_score"
        ].filter(Boolean);
      };

      return {
        aggs: {
          top_per_company: {
            terms: {
              field: "company.id",
              size: 10, // 10 companies is 30 jobs total
              order: { [filters.sort_key]: "desc" }
            },
            aggs: {
              tops: {
                top_hits: {
                  sort: sortValues(),
                  size: 3
                }
              },
              recently_updated: {
                max: {
                  field: "updated_at"
                }
              },
              newest: {
                max: {
                  field: "created_at"
                }
              }
            }
          }
        },
        sort: sortValues(),
        query: {
          bool: {
            must: [
              { match: { visible: true } },
              { match: { "company.application_status": "published" } },
              filters.commitment.length && {
                terms: { "commitment.id": filters.commitment }
              },
              filters.skill_tag_id.length && {
                terms: { "skill_tags.id": filters.skill_tag_id }
              },
              filters.company_id && {
                match: { "company.id": filters.company_id }
              },
              filters.remote_friendly && {
                bool: {
                  should: [
                    { match: { remote: true } },
                    { match: { remote_friendly: true } }
                  ]
                }
              },
              filters.coords.lat &&
                filters.coords.lng && {
                  geo_distance: {
                    distance: filters.radius,
                    coordinates: `${filters.coords.lat},${filters.coords.lng}`
                  }
                },
              filters.input_text !== "" && {
                query_string: {
                  query: preprocessQueryString(filters.input_text),
                  default_operator: "AND",
                  fuzziness: 1,
                  fuzzy_prefix_length: 0,
                  fuzzy_max_expansions: 10
                }
              }
            ].filter(Boolean)
          }
        }
      };
    }
  },

  methods: {
    generateFilters() {
      return [
        {
          key: "input_text",
          initial: getURLParams().get("input_text"),
          default: ""
        },
        {
          key: "sort_key",
          initial: getURLParams().get("sort_key"),
          default: "newest"
        },
        {
          key: "skill_tag_id",
          urlKey: "skill_id",
          initial: getURLParams().getAll("skill_id"),
          default: []
        },
        {
          key: "commitment",
          initial: getURLParams().getAll("commitment"),
          default: []
        },
        {
          key: "company_id",
          initial: getURLParams().get("company_id"),
          default: null
        },
        {
          key: "remote_friendly",
          initial: getURLParams().get("remote_friendly"),
          default: false
        },
        {
          key: "radius",
          initial: getURLParams().get("radius"),
          default: "5 miles"
        },
        {
          key: "coords",
          initial: JSON.parse(getURLParams().get("coords")),
          default: { lat: null, lng: null }
        },
        {
          key: "location",
          initial: getURLParams().get("location"),
          default: null
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
.jobs {
  position: relative;

  &__filters-list {
    display: flex;
    margin: 0 0 12px;
    align-items: baseline;
    @media (max-width: 800px) {
      flex-direction: column;
    }
  }
  &__filter {
    flex: 1 1 auto;
    &--select {
      width: 25%;
      @media (max-width: 800px) {
        width: 100%;
      }
    }
    &--checkbox {
      flex: 0 0 auto;
    }
    & + & {
      margin-left: 12px;
      @media (max-width: 800px) {
        margin-left: 0;
        margin-top: 12px;
      }
    }
  }

  &__list {
    position: relative;
    transition: opacity 0.3s ease;
    &--is-loading {
      opacity: 0.6;
      &::before {
        @include loading_before;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/pages/jobs.vue?43adc425